import React from 'react';
import { Check } from 'lucide-react';
import clsx from 'clsx';
import styles from './Checkbox.module.css';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  ref?: React.Ref<HTMLInputElement>;
  label?: string | React.ReactNode;
  error?: string | React.ReactNode;
  wrapperClassName?: string;
  dataTestId?: string;
}

function Checkbox({
  ref,
  label,
  error,
  required,
  className,
  wrapperClassName,
  checked,
  disabled,
  id,
  dataTestId,
  ...props
}: CheckboxProps) {
  // Generate ID for label association if not provided
  const generatedId = React.useId();
  const checkboxId = id || `checkbox-${generatedId}`;

  return (
    <div className={clsx(styles.checkboxContainer, wrapperClassName)}>
      <label
        htmlFor={checkboxId}
        className={clsx(
          styles.checkboxWrapper,
          disabled && styles.checkboxWrapperDisabled,
          error && styles.checkboxWrapperError
        )}
        data-test-id={dataTestId ? `${dataTestId}-label` : undefined}
      >
        <input
          id={checkboxId}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          className={className}
          aria-describedby={error ? `${checkboxId}-error` : undefined}
          data-test-id={dataTestId ? `${dataTestId}-input` : undefined}
          {...props}
          ref={ref}
        />

        <span className={styles.checkboxCustom}>{checked && <Check size={12} />}</span>

        {label && (
          <span className={styles.checkboxLabel}>
            {label}
            {required && <span className={styles.required}>*</span>}
          </span>
        )}
      </label>

      {error && (
        <div id={`${checkboxId}-error`} className={styles.errorMessage} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

export default Checkbox;
